import dayjs from "dayjs";
import { QUERY_TYPE } from "~/features/orders/common/queryTypes";
import { getAliases } from "~/domains/product/components/ProductAliases";
import { type OrderbookRowItem } from "~/features/orderbook/models/orderbookModels";
import {
  OrderbookConfirmedDeliveryStatus,
  OrderbookLineItemStatus,
  type OrderbookSearchBoxData,
} from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchBox";
import {
  downloadOrderbookReport,
  type OrderbookForm,
  type ReportType,
} from "~/features/orderbook/api/downloadOrderbookReport";

type FilterFunc = (item: OrderbookRowItem) => boolean;

const FILTERS: { [key: string]: (value: string) => FilterFunc } = {
  [QUERY_TYPE.PURCHASE_NO]: (value) => (row) => row.customerOrderNo.toLowerCase().includes(value),
  [QUERY_TYPE.SALES_NO]: (value) => (row) => row.schaefflerOrderNo.toLowerCase().includes(value),
  [QUERY_TYPE.DESIGNATION]: (value) => (row) => row.product.designation.toLowerCase().includes(value),
  [QUERY_TYPE.CUSTOMER_MATERIAL_NO]: (value) => (row) =>
    getAliases(row.product, "all").join(", ").toLowerCase().includes(value),
  [QUERY_TYPE.SAP_MATERIAL_NO]: (value) => (row) => row.product.sapMaterialNumber.toLowerCase().includes(value),
};

export function filterOrderbookRows(rows: OrderbookRowItem[], filterConfig?: OrderbookSearchBoxData) {
  if (!filterConfig) {
    return rows;
  }

  const filters: FilterFunc[] = [];
  if (filterConfig.query) {
    const filterFunc = FILTERS[filterConfig.queryType];
    if (!filterFunc) {
      console.warn("Missing filter function for query type: " + filterConfig.queryType);
    } else {
      filters.push(filterFunc(filterConfig.query.toLowerCase()));
    }
  }

  if (filterConfig.dateFrom) {
    const dateFrom = dayjs(filterConfig.dateFrom);
    filters.push((item) => !dateFrom.isAfter(item.requestedDeliveryDate));
  }

  if (filterConfig.dateTo) {
    const dateTo = dayjs(filterConfig.dateTo);
    filters.push((item) => !dateTo.isBefore(item.requestedDeliveryDate));
  }

  if (filterConfig.lineStatus === OrderbookLineItemStatus.NEW) {
    filters.push((item) => item.isNew);
  }

  if (filterConfig.lineStatus === OrderbookLineItemStatus.UPDATED) {
    filters.push((item) => item.isUpdated);
  }

  if (filterConfig.lineStatus === OrderbookLineItemStatus.NO_CHANGE) {
    filters.push((item) => !item.isUpdated && !item.isNew);
  }

  if (filterConfig.confirmedLines.length > 0) {
    const hasConfirmed = filterConfig.confirmedLines.includes(OrderbookConfirmedDeliveryStatus.CONFIRMED);
    const hasUnconfirmed = filterConfig.confirmedLines.includes(OrderbookConfirmedDeliveryStatus.UNCONFIRMED);
    if (hasConfirmed && !hasUnconfirmed) {
      filters.push((item) => !!item.curDeliveryDate);
    } else if (hasUnconfirmed && !hasConfirmed) {
      filters.push((item) => !item.curDeliveryDate);
    }
  }

  return rows.filter((item) => filters.every((filter) => filter(item)));
}

export function downloadOrderbook(
  reportType: ReportType,
  filterConfig: OrderbookSearchBoxData | undefined,
): () => void {
  return () => {
    if (!filterConfig) {
      return;
    }

    const apiBody: OrderbookForm = {
      filterByOption: filterConfig.queryType,
      filterByValue: filterConfig ? filterConfig.query : "",
      startDate: filterConfig ? filterConfig.dateFrom : undefined,
      endDate: filterConfig ? filterConfig.dateTo : undefined,
      lineStatus: filterConfig ? filterConfig.lineStatus : undefined,
      // TODO - Change to multiple when API supports it
      deliveryDate: filterConfig ? filterConfig.confirmedLines[0] : undefined,
    };

    downloadOrderbookReport(apiBody, reportType);
  };
}
